import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { AuthState, AuthContextType, GoogleSignInResponse, AuthProvider as AuthProviderType, User } from '../types/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { signInWithCustomToken, signOut } from 'firebase/auth';
import { db, auth } from '../firebase';
import { useToast } from './ToastContext';
import { API_BASE_URL } from '../config';
import { apiClient } from '../utils/apiClient';

// Initial state
const initialState: AuthState = {
  isAuthenticated: false,
  user: null,
  loading: true, // Start with loading to check existing auth
};

// Action types
type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'LOGIN_SUCCESS'; payload: { phoneE164?: string; whatsappNumberE164?: string; telegramNumberE164?: string; userId: string; newUser: boolean; email?: string; emailVerified?: boolean; providers?: AuthProviderType[]; status?: 'active' | 'request_delete'; createdAt?: string; updatedAt?: string; lastLoginAt?: string; profile?: { username?: string; firstName?: string; lastName?: string; email?: string; profilePhoto?: string; instagramLink?: string; tiktokLink?: string } } }
  | { type: 'UPDATE_PROFILE'; payload: { profile: any; createdAt?: string; updatedAt: string; lastLoginAt?: string } }
  | { type: 'REFRESH_VERIFICATION_STATUS'; payload: { emailVerified?: boolean; whatsappNumberE164?: string; telegramNumberE164?: string; providers?: AuthProviderType[]; status?: 'active' | 'request_delete'; profile?: any; updatedAt?: string; lastLoginAt?: string } }
  | { type: 'LOGOUT' }
  | { type: 'SET_ERROR'; payload: string };

// Reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, loading: action.payload };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        isAuthenticated: true,
        user: {
          phoneE164: action.payload.phoneE164,
          whatsappNumberE164: action.payload.whatsappNumberE164,
          telegramNumberE164: action.payload.telegramNumberE164,
          userId: action.payload.userId,
          newUser: action.payload.newUser,
          email: action.payload.email,
          emailVerified: action.payload.emailVerified,
          providers: action.payload.providers,
          status: action.payload.status,
          createdAt: action.payload.createdAt,
          updatedAt: action.payload.updatedAt,
          lastLoginAt: action.payload.lastLoginAt,
          profile: action.payload.profile,
        },
        loading: false,
      };
    case 'UPDATE_PROFILE':
      return {
        ...state,
        user: state.user ? {
          ...state.user,
          profile: action.payload.profile,
          createdAt: action.payload.createdAt || state.user.createdAt,
          updatedAt: action.payload.updatedAt,
          lastLoginAt: action.payload.lastLoginAt || state.user.lastLoginAt,
        } : null,
      };
    case 'REFRESH_VERIFICATION_STATUS':
      return {
        ...state,
        user: state.user ? {
          ...state.user,
          emailVerified: action.payload.emailVerified !== undefined ? action.payload.emailVerified : state.user.emailVerified,
          whatsappNumberE164: action.payload.whatsappNumberE164 !== undefined ? action.payload.whatsappNumberE164 : state.user.whatsappNumberE164,
          telegramNumberE164: action.payload.telegramNumberE164 !== undefined ? action.payload.telegramNumberE164 : state.user.telegramNumberE164,
          providers: action.payload.providers || state.user.providers,
          status: action.payload.status || state.user.status,
          profile: action.payload.profile || state.user.profile,
          updatedAt: action.payload.updatedAt || state.user.updatedAt,
          lastLoginAt: action.payload.lastLoginAt || state.user.lastLoginAt,
        } : null,
      };
    case 'LOGOUT':
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        loading: false,
      };
    case 'SET_ERROR':
      return { ...state, loading: false };
    default:
      return state;
  }
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
export function AuthProvider({ children }: { children: ReactNode }) {
  const [authState, dispatch] = useReducer(authReducer, initialState);
  const { showToast } = useToast();

  // Check for existing authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const storedAuth = localStorage.getItem('hey-sri-lanka-auth');
        if (storedAuth) {
          const { phoneE164, whatsappNumberE164, telegramNumberE164, userId, newUser, email, emailVerified, providers, status, createdAt, updatedAt, lastLoginAt, profile } = JSON.parse(storedAuth);
          dispatch({
            type: 'LOGIN_SUCCESS',
            payload: { phoneE164, whatsappNumberE164, telegramNumberE164, userId, newUser, email, emailVerified, providers, status, createdAt, updatedAt, lastLoginAt, profile },
          });
        } else {
          dispatch({ type: 'SET_LOADING', payload: false });
        }
      } catch (error) {
        dispatch({ type: 'SET_LOADING', payload: false });
      }
    };

    checkAuth();
  }, []);

  // ON-DEMAND listener for verification (no automatic listeners)
  // This will be started only when user clicks WhatsApp/Telegram verification button
  const startVerificationListener = (verificationType: 'whatsapp' | 'telegram') => {
    if (!authState.user?.userId) {
      return () => {}; // Return no-op cleanup
    }

    const userDocRef = doc(db, 'users', authState.user.userId);
    let isFirstSnapshot = true;
    let hasDetectedVerification = false;
    
    const unsubscribe = onSnapshot(
      userDocRef,
      (docSnapshot) => {
        if (docSnapshot.exists() && !hasDetectedVerification) {
          const userData = docSnapshot.data();
          
          // Check for verification based on type
          const isNowVerified = verificationType === 'whatsapp' 
            ? userData.whatsappNumberE164 
            : userData.telegramNumberE164;
          
          const wasNotVerifiedBefore = verificationType === 'whatsapp'
            ? !authState.user?.whatsappNumberE164
            : !authState.user?.telegramNumberE164;
          
          // Detect if verification just happened (skip first snapshot)
          if (!isFirstSnapshot && isNowVerified && wasNotVerifiedBefore) {
            hasDetectedVerification = true;
            
            // Convert Firestore Timestamps to ISO strings
            const createdAt = userData.createdAt?.toDate?.()?.toISOString() || authState.user!.createdAt;
            const updatedAt = userData.updatedAt?.toDate?.()?.toISOString() || authState.user!.updatedAt;
            const lastLoginAt = userData.lastLoginAt?.toDate?.()?.toISOString() || authState.user!.lastLoginAt;
            
            // Update user state
            const updatedUser = {
              ...authState.user!,
              whatsappNumberE164: userData.whatsappNumberE164 !== undefined ? userData.whatsappNumberE164 : authState.user!.whatsappNumberE164,
              telegramNumberE164: userData.telegramNumberE164 !== undefined ? userData.telegramNumberE164 : authState.user!.telegramNumberE164,
              profile: userData.profile || authState.user!.profile,
              providers: userData.providers || authState.user!.providers || [],
              status: userData.status || authState.user!.status,
              emailVerified: userData.emailVerified !== undefined ? userData.emailVerified : authState.user!.emailVerified,
              createdAt,
              updatedAt,
              lastLoginAt,
            };
            
            // Update localStorage
            localStorage.setItem('hey-sri-lanka-auth', JSON.stringify(updatedUser));
            
            // Dispatch update
            dispatch({
              type: 'LOGIN_SUCCESS',
              payload: updatedUser,
            });
            
            // Show success toast
            const verifiedNumber = verificationType === 'whatsapp' 
              ? userData.whatsappNumberE164 
              : userData.telegramNumberE164;
            const isLocalNumber = verifiedNumber?.startsWith('+94');
            
            if (isLocalNumber) {
              showToast(
                `✅ ${verificationType === 'whatsapp' ? 'WhatsApp' : 'Telegram'} verified! Note: Extended features are for inbound travelers only.`,
                'warning',
                6000
              );
            } else {
              showToast(
                `🎉 ${verificationType === 'whatsapp' ? 'WhatsApp' : 'Telegram'} verified! Full access unlocked!`,
                'success',
                5000
              );
            }
            
            // Stop listening immediately after detection
            unsubscribe();
          }
          
          isFirstSnapshot = false;
        }
      },
      (error) => {
        // Listener error
      }
    );

    // Auto-cleanup after 5 minutes (300,000 ms)
    const timeoutId = setTimeout(() => {
      if (!hasDetectedVerification) {
        unsubscribe();
      }
    }, 5 * 60 * 1000);

    // Return cleanup function that clears both listener and timeout
    return () => {
      clearTimeout(timeoutId);
      unsubscribe();
    };
  };

  const updateProfile = async (profileData: any): Promise<void> => {
    if (!authState.user) {
      throw new Error('No user logged in');
    }

    try {
      const response = await apiClient.put(`/api/auth/profile/${authState.user.userId}`, profileData);

      if (!response.ok) {
        const data = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(data.error || 'Failed to update profile');
      }

      const data = await response.json();

      // Update in state
      dispatch({
        type: 'UPDATE_PROFILE',
        payload: {
          profile: data.profile,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          lastLoginAt: data.lastLoginAt,
        },
      });

      // Update in localStorage
      const storedAuth = localStorage.getItem('hey-sri-lanka-auth');
      if (storedAuth) {
        const authData = JSON.parse(storedAuth);
        authData.profile = data.profile;
        authData.createdAt = data.createdAt;
        authData.updatedAt = data.updatedAt;
        authData.lastLoginAt = data.lastLoginAt;
        localStorage.setItem('hey-sri-lanka-auth', JSON.stringify(authData));
      }
    } catch (error) {
      throw error;
    }
  };

  const signInWithGoogle = async (credential: string): Promise<GoogleSignInResponse> => {
    dispatch({ type: 'SET_LOADING', payload: true });
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/google-signin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Google sign-in failed');
      }
      
      // Check if account was reinstated from deletion request
      const wasReinstated = data.status === 'active' && data.linked;
      
      // Sign in to Firebase Auth with custom token
      if (data.firebaseToken) {
        await signInWithCustomToken(auth, data.firebaseToken);
      }
      
      // Store auth data
      const authData = {
        email: data.email,
        emailVerified: data.emailVerified,
        userId: data.userId,
        newUser: data.newUser,
        profile: data.profile,
        providers: data.providers,
        whatsappNumberE164: data.whatsappNumberE164,
        telegramNumberE164: data.telegramNumberE164,
        status: data.status,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
        lastLoginAt: data.lastLoginAt,
      };
      
      localStorage.setItem('hey-sri-lanka-auth', JSON.stringify(authData));
      
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: authData,
      });
      
      // Show toast if account was reinstated
      if (wasReinstated) {
        showToast(
          '🎉 Welcome back! Your account deletion has been cancelled.',
          'success',
          5000
        );
      }
      return data;
    } catch (error) {
      dispatch({ type: 'SET_ERROR', payload: (error as Error).message });
      throw error;
    }
  };

  const refreshUserProfile = async (): Promise<User> => {
    if (!authState.user?.userId) {
      throw new Error('No user logged in');
    }

    try {
      // Force cache bypass with timestamp parameter
      const response = await apiClient.get(`/api/auth/profile/${authState.user.userId}?t=${Date.now()}`, {
        headers: {
          'Cache-Control': 'no-cache'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch user profile');
      }

      const data = await response.json();
      
      // Update localStorage
      const storedAuth = localStorage.getItem('hey-sri-lanka-auth');
      if (storedAuth) {
        const authData = JSON.parse(storedAuth);
        const updatedUser = {
          ...authData,
          whatsappNumberE164: data.whatsappNumberE164,
          providers: data.providers,
          profile: data.profile,
          status: data.status,
          emailVerified: data.emailVerified,
          createdAt: data.createdAt,
          updatedAt: data.updatedAt,
          lastLoginAt: data.lastLoginAt,
        };
        localStorage.setItem('hey-sri-lanka-auth', JSON.stringify(updatedUser));
        
        // Update state using REFRESH_VERIFICATION_STATUS to avoid recreating user object
        // This preserves chat history and other contexts that depend on user reference
        dispatch({
          type: 'REFRESH_VERIFICATION_STATUS',
          payload: {
            emailVerified: data.emailVerified,
            whatsappNumberE164: data.whatsappNumberE164,
            telegramNumberE164: data.telegramNumberE164,
            providers: data.providers,
            status: data.status,
            profile: data.profile,
            updatedAt: data.updatedAt,
            lastLoginAt: data.lastLoginAt,
          },
        });
        
        // Return the updated user data
        return updatedUser;
      }
      
      throw new Error('No stored auth data');
    } catch (error) {
      throw error;
    }
  };

  const deleteAccount = async (): Promise<void> => {
    if (!authState.user?.userId) {
      throw new Error('No user logged in');
    }

    try {
      const response = await apiClient.post(`/api/auth/delete-account/${authState.user.userId}`);

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete account');
      }
      
      // Logout the user immediately after requesting deletion
      await logout();
    } catch (error) {
      throw error;
    }
  };

  const logout = async (): Promise<void> => {
    // Sign out from Firebase Auth
    try {
      await signOut(auth);
    } catch (error) {
      // Continue with logout even if Firebase signOut fails
    }
    
    localStorage.removeItem('hey-sri-lanka-auth');
    dispatch({ type: 'LOGOUT' });
  };

  // ===== EMAIL/PASSWORD AUTHENTICATION =====

  const checkEmailExists = async (email: string): Promise<{ exists: boolean; authMethod: string | null }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/check-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      if (!response.ok) {
        throw new Error('Failed to check email');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  };

  const registerWithEmail = async (email: string, password: string): Promise<User> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Registration failed');
      }

      // Sign in to Firebase Auth with custom token
      if (data.firebaseToken) {
        await signInWithCustomToken(auth, data.firebaseToken);
      }

      // Store in localStorage (without firebaseToken - it's one-time use)
      const { firebaseToken, ...authData } = data;
      localStorage.setItem('hey-sri-lanka-auth', JSON.stringify(authData));

      // Update state
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: authData,
      });

      return authData;
    } catch (error) {
      throw error;
    }
  };

  const loginWithEmail = async (email: string, password: string): Promise<User> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Login failed');
      }

      // Sign in to Firebase Auth with custom token
      if (data.firebaseToken) {
        await signInWithCustomToken(auth, data.firebaseToken);
      }

      // Store in localStorage (without firebaseToken - it's one-time use)
      const { firebaseToken, ...authData } = data;
      localStorage.setItem('hey-sri-lanka-auth', JSON.stringify(authData));

      // Update state
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: authData,
      });

      // Show toast if account was reinstated
      if (authData.wasReinstated) {
        showToast('Welcome back! Your account has been reactivated.', 'success');
      }

      return authData;
    } catch (error) {
      throw error;
    }
  };

  const forgotPassword = async (email: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send reset email');
      }

      return data;
    } catch (error) {
      throw error;
    }
  };

  const resetPassword = async (token: string, newPassword: string): Promise<{ success: boolean; message: string }> => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, newPassword }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset password');
      }

      return data;
    } catch (error) {
      throw error;
    }
  };

  const resendVerificationEmail = async (): Promise<{ success: boolean; message: string }> => {
    if (!authState.user?.userId) {
      throw new Error('No user logged in');
    }

    try {
      const response = await apiClient.post(`/api/auth/resend-verification/${authState.user.userId}`);

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to resend verification email');
      }

      showToast(data.message || 'Verification email sent!', 'success');
      return data;
    } catch (error) {
      throw error;
    }
  };

  const value: AuthContextType = {
    authState,
    signInWithGoogle,
    updateProfile,
    refreshUserProfile,
    deleteAccount,
    logout,
    checkEmailExists,
    registerWithEmail,
    loginWithEmail,
    forgotPassword,
    resetPassword,
    resendVerificationEmail,
    startVerificationListener, // ON-DEMAND listener
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// Hook to use auth context
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
